export const BORDER_FRAME_PRESETS = {
  double: {
    label: "Double line",
    draw: (color) =>
      `<rect x='3' y='3' width='84' height='84' fill='none' stroke='${color}' stroke-width='6'/><rect x='16' y='16' width='58' height='58' fill='none' stroke='${color}' stroke-width='2'/>`,
  },
  dots: {
    label: "Dots",
    draw: (color) =>
      `<g fill='${color}'><circle cx='9' cy='9' r='6'/><circle cx='81' cy='9' r='6'/><circle cx='9' cy='81' r='6'/><circle cx='81' cy='81' r='6'/><circle cx='45' cy='9' r='4.5'/><circle cx='45' cy='81' r='4.5'/><circle cx='9' cy='45' r='4.5'/><circle cx='81' cy='45' r='4.5'/></g>`,
  },
  corners: {
    label: "Corner brackets",
    draw: (color) =>
      `<g fill='none' stroke='${color}' stroke-width='5' stroke-linecap='round'><path d='M4 30V12a8 8 0 0 1 8-8h18'/><path d='M60 4h18a8 8 0 0 1 8 8v18'/><path d='M86 60v18a8 8 0 0 1-8 8H60'/><path d='M30 86H12a8 8 0 0 1-8-8V60'/></g>`,
  },
  rule: {
    label: "Top and bottom rules",
    draw: (color) =>
      `<g stroke='${color}' stroke-width='6' stroke-linecap='round'><path d='M3 6h84M3 84h84'/></g>`,
  },
};

export const DEFAULT_BORDER_FRAME = {
  type: "line",
  width: 1,
  style: "solid",
  color: "#6F6961",
  radius: 0,
  preset: "double",
  src: "",
  slice: 30,
  repeat: "round",
  pad: 0,
  opacity: 1,
};

export const frameUri = (preset = "double", color = "#6F6961") => {
  const frame = BORDER_FRAME_PRESETS[preset] || BORDER_FRAME_PRESETS.double;
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='90' height='90' viewBox='0 0 90 90'>${frame.draw(color)}</svg>`,
  )}`;
};

export const frameSource = (frame) =>
  frame?.preset === "custom" ? frame.src || "" : frameUri(frame?.preset, frame?.color);

/**
 * Percentages keep corner size stable across any image resolution.
 * Built-in 90×90 frames are drawn for a fixed third; custom uses the stored slice %.
 * Matches design-tool-editor.html `sliceOf`.
 */
export const formatBorderImageSlice = (slice, preset) => {
  if (preset && preset !== "custom") return "33.333%";
  return `${Math.max(1, Number(slice) || 30)}%`;
};

export const buildElementFrameStyle = (style = {}) => {
  const width = Math.max(0, Number(style.ElementBorderWidth) || 0);
  const padding = Math.max(0, Number(style.ElementInnerPadding) || 0);
  const common = { boxSizing: "border-box", padding: `${padding}px` };

  if (style.ElementBorderIsImage === true && style.ElementBorderImage && width > 0) {
    return {
      ...common,
      border: `${width}px solid transparent`,
      borderImage: `url("${style.ElementBorderImage}") ${formatBorderImageSlice(
        style.ElementBorderImageSlice,
        style.ElementBorderImagePreset,
      )} ${style.ElementBorderImageRepeat || "round"}`,
      borderRadius: 0,
      overflow: "visible",
    };
  }

  const radius = Math.max(0, Number(style.ElementBorderRadius) || 0);
  return {
    ...common,
    border:
      width > 0
        ? `${width}px ${style.ElementBorderPatteren || "solid"} ${
            style.ElementBorderColor || "#000000"
          }`
        : "none",
    borderImage: "none",
    borderRadius: `${radius}px`,
    overflow: radius > 0 ? "hidden" : undefined,
  };
};

export const buildOutputFrameVariables = (style = {}) => ({
  "--output-frame-width": `${Math.max(0, Number(style.ElementBorderWidth) || 0)}px`,
  "--output-frame-padding": `${Math.max(0, Number(style.ElementInnerPadding) || 0)}px`,
  "--output-frame-radius": `${Math.max(0, Number(style.ElementBorderRadius) || 0)}px`,
  "--output-frame-overflow":
    Math.max(0, Number(style.ElementBorderRadius) || 0) > 0 ? "hidden" : "visible",
  "--output-frame-image": `url("${style.ElementBorderImage || ""}") ${formatBorderImageSlice(
    style.ElementBorderImageSlice,
    style.ElementBorderImagePreset,
  )} ${style.ElementBorderImageRepeat || "round"}`,
});

export const inheritLastRowCellStyles = (style = {}, tableDetails = [], rowIndex) => {
  const isLastRow = rowIndex === tableDetails.length - 1;
  if (!isLastRow) return style;

  const width = Math.max(0, Number(style.CellBorderWidth) || 0);
  if (width > 0) return style;

  for (let i = tableDetails.length - 2; i >= 0; i -= 1) {
    const cells = tableDetails[i]?.RowCells || [];
    for (const cell of cells) {
      const sample = cell?.CellStyles;
      const sampleWidth = Math.max(0, Number(sample?.CellBorderWidth) || 0);
      if (sampleWidth > 0) {
        return {
          ...style,
          CellBorderWidth: sampleWidth,
          CellBorderPatteren:
            style.CellBorderPatteren && style.CellBorderPatteren !== "none"
              ? style.CellBorderPatteren
              : sample.CellBorderPatteren || "solid",
          CellBorderColor: style.CellBorderColor || sample.CellBorderColor || "#000000",
        };
      }
    }
  }

  const pattern = style.CellBorderPatteren;
  if (pattern && pattern !== "none") {
    return {
      ...style,
      CellBorderWidth: 1,
      CellBorderColor: style.CellBorderColor || "#000000",
    };
  }
  return style;
};

export const buildCellFrameStyle = (style = {}) => {
  const width = Math.max(0, Number(style.CellBorderWidth) || 0);
  const padding = Math.max(0, Number(style.CellInnerPadding) || 0);
  if (style.CellBorderIsImage === true && style.CellBorderImage && width > 0) {
    return {
      boxSizing: "border-box",
      padding: `${padding}px`,
      border: `${width}px solid transparent`,
      borderImage: `url("${style.CellBorderImage}") ${formatBorderImageSlice(
        style.CellBorderImageSlice,
        style.CellBorderImagePreset,
      )} ${style.CellBorderImageRepeat || "round"}`,
      borderRadius: 0,
      overflow: "visible",
    };
  }
  const radius = Math.max(0, Number(style.CellBorderRadius) || 0);
  return {
    boxSizing: "border-box",
    padding: `${padding}px`,
    border:
      width > 0
        ? `${width}px ${style.CellBorderPatteren || "solid"} ${
            style.CellBorderColor || "#000000"
          }`
        : "none",
    borderImage: "none",
    borderRadius: `${radius}px`,
    // Collapsed last-row borders sit on the table edge and disappear when the
    // cell/wrapper clips overflow to honor radius.
    overflow: "visible",
  };
};

export const buildTableCellFrameStyle = (style, tableDetails, rowIndex) =>
  buildCellFrameStyle(inheritLastRowCellStyles(style, tableDetails, rowIndex));
